import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ComposedChart, Bar, Line, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Types ────────────────────────────────────────────────────────────────────
interface Txn {
  id: number;
  transaction_date: string;
  amount: string | number;
  transaction_type: 'DEBIT' | 'CREDIT';
  payment_mode: string;
  ledger_category_name?: string | null;
  ledger_subcategory_name?: string | null;
}

interface MonthAgg {
  ym: string;
  label: string;
  billing: number;      // billing collections (paid this month)
  ledgerIncome: number; // ledger CREDITs
  income: number;       // billing + ledgerIncome
  spend: number;        // ledger DEBITs
  net: number;          // income - spend
}

const INCOME_COLOR = '#2563eb';
const SPEND_COLOR = '#ef4444';
const NET_COLOR = '#16a34a';

// ── Helpers ──────────────────────────────────────────────────────────────────
function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const ymOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const addMonth = (ym: string, delta: number) => {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${pad2((idx % 12) + 1)}`;
};
const monthsEndingAt = (ym: string, n: number) =>
  Array.from({ length: n }, (_, i) => addMonth(ym, -(n - 1 - i)));
const labelOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};
const firstDay = (ym: string) => `${ym}-01`;
const lastDay = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${pad2(new Date(y, m, 0).getDate())}`;
};

const fmtL = (n: number) => {
  const a = Math.abs(n);
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const fmtFull = (n: number) => (n < 0 ? '-' : '') + '₹' + Math.round(Math.abs(n)).toLocaleString('en-IN');

function linfitNext(series: number[]): number | null {
  const n = series.length;
  if (n < 2) return null;
  const xs = series.map((_, i) => i);
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = series.reduce((a, b) => a + b, 0) / n;
  let denom = 0, num = 0;
  for (let i = 0; i < n; i++) { denom += (xs[i] - mx) ** 2; num += (xs[i] - mx) * (series[i] - my); }
  if (denom === 0) return Math.max(my, 0);
  const slope = num / denom;
  return Math.max(slope * n + (my - slope * mx), 0);
}

type DeltaTone = 'good' | 'bad' | 'neutral';
function buildDelta(cur: number, prev: number, goodUp: boolean): { label: string; tone: DeltaTone } {
  if (prev === 0 && cur === 0) return { label: 'no change', tone: 'neutral' };
  if (prev === 0) return { label: 'new', tone: 'neutral' };
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  const up = pct >= 0;
  const tone: DeltaTone = Math.abs(pct) < 0.05 ? 'neutral' : goodUp ? (up ? 'good' : 'bad') : (up ? 'bad' : 'good');
  return { label: `${up ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`, tone };
}

// ── Tooltip ──────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.7rem 0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.82rem' }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: '#0f172a' }}>{label}</div>
      {payload.filter((p: any) => p.value != null).map((p: any) => (
        <div key={p.name} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: p.color || p.stroke }} />
          <span style={{ color: '#64748b' }}>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
const KPICard: React.FC<{ label: string; value: string; color: string; delta?: { label: string; tone: DeltaTone }; compareLabel?: string }> =
({ label, value, color, delta, compareLabel }) => {
  const tone = delta?.tone;
  const toneBg = tone === 'good' ? '#dcfce7' : tone === 'bad' ? '#fee2e2' : '#f1f5f9';
  const toneColor = tone === 'good' ? '#15803d' : tone === 'bad' ? '#b91c1c' : '#64748b';
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem 1.35rem', border: '1px solid #eef1f6', boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 14px 30px -22px rgba(15,23,42,0.35)', position: 'relative', overflow: 'hidden' }}>
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: color, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
        <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</div>
      {delta && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: toneColor, background: toneBg, padding: '3px 9px', borderRadius: 999 }}>{delta.label}</span>
          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>vs {compareLabel}</span>
        </div>
      )}
    </div>
  );
};

// ── Soft stat tile (used inside the 12-month panel) ──────────────────────────
const StatTile: React.FC<{ label: string; value: string; note?: string; color?: string }> = ({ label, value, note, color }) => (
  <div style={{ background: '#f8fafc', border: '1px solid #eef1f6', borderRadius: 12, padding: '0.9rem 1.05rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: color || '#94a3b8' }} />
      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginTop: 7 }}>{value}</div>
    {note && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{note}</div>}
  </div>
);

// ── Main ─────────────────────────────────────────────────────────────────────
const HISTORY = 12;
const FORECAST = 3;

const FinancialDashboard: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(ymOf(new Date()));
  const [compareMode, setCompareMode] = useState<'prev' | 'custom'>('prev');
  const [customCompare, setCustomCompare] = useState(addMonth(ymOf(new Date()), -1));
  const [txns, setTxns] = useState<Txn[]>([]);
  const [collections, setCollections] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const windowMonths = useMemo(() => monthsEndingAt(selectedMonth, HISTORY), [selectedMonth]);
  const compareYm = compareMode === 'prev' ? addMonth(selectedMonth, -1) : customCompare;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // widen the window so a custom comparison month outside the 12-mo span is still covered
      const earliest = windowMonths[0] < compareYm ? windowMonths[0] : compareYm;
      const start = firstDay(earliest);
      const end = lastDay(selectedMonth >= compareYm ? selectedMonth : compareYm);
      const [txnRes, colRes] = await Promise.all([
        axios.get(`${API}/api/financial/transactions`, { params: { date_from: start, date_to: `${end}T23:59:59` }, headers: getAuthHeader() }),
        axios.get(`${API}/api/billing/collections-by-month`, { params: { start, end }, headers: getAuthHeader() }),
      ]);
      setTxns(Array.isArray(txnRes.data) ? txnRes.data : []);
      const cmap: Record<string, number> = {};
      (Array.isArray(colRes.data) ? colRes.data : []).forEach((r: any) => { cmap[r.month] = Number(r.amount) || 0; });
      setCollections(cmap);
    } catch {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, [windowMonths, compareYm, selectedMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Aggregate all fetched transactions into per-month maps, expose an aggOf(ym) helper.
  const { aggOf, monthly } = useMemo(() => {
    const spendByM: Record<string, number> = {};
    const ledgerIncByM: Record<string, number> = {};
    txns.forEach((t) => {
      const ym = String(t.transaction_date).slice(0, 7);
      const amt = Number(t.amount) || 0;
      if (t.transaction_type === 'DEBIT') spendByM[ym] = (spendByM[ym] || 0) + amt;
      else ledgerIncByM[ym] = (ledgerIncByM[ym] || 0) + amt;
    });
    const aggOf = (ym: string): MonthAgg => {
      const billing = collections[ym] || 0;
      const li = ledgerIncByM[ym] || 0;
      const sp = spendByM[ym] || 0;
      const income = billing + li;
      return { ym, label: labelOf(ym), billing, ledgerIncome: li, income, spend: sp, net: income - sp };
    };
    return { aggOf, monthly: windowMonths.map(aggOf) };
  }, [txns, collections, windowMonths]);

  const current = aggOf(selectedMonth);
  const compare = aggOf(compareYm);

  // Projection — linear trend over the 12-month window, extended FORECAST months
  const projection = useMemo(() => {
    let inc = monthly.map((m) => m.income);
    let sp = monthly.map((m) => m.spend);
    const out: { ym: string; label: string; income: number; spend: number; net: number }[] = [];
    for (let i = 1; i <= FORECAST; i++) {
      const pi = linfitNext(inc);
      const ps = linfitNext(sp);
      const fi = pi ?? (inc.length ? inc.reduce((a, b) => a + b, 0) / inc.length : 0);
      const fs = ps ?? (sp.length ? sp.reduce((a, b) => a + b, 0) / sp.length : 0);
      inc = [...inc, fi]; sp = [...sp, fs];
      const ym = addMonth(selectedMonth, i);
      out.push({ ym, label: labelOf(ym), income: Math.round(fi), spend: Math.round(fs), net: Math.round(fi - fs) });
    }
    return out;
  }, [monthly, selectedMonth]);

  const hasData = monthly.some((m) => m.income || m.spend);

  // Window (last HISTORY months) roll-up metrics
  const windowAgg = useMemo(() => {
    const income = monthly.reduce((a, m) => a + m.income, 0);
    const spend = monthly.reduce((a, m) => a + m.spend, 0);
    const billing = monthly.reduce((a, m) => a + m.billing, 0);
    const ledgerIncome = monthly.reduce((a, m) => a + m.ledgerIncome, 0);
    const months = monthly.length || 1;
    const base: MonthAgg = monthly[0] || { ym: '', label: '—', billing: 0, ledgerIncome: 0, income: 0, spend: 0, net: 0 };
    const bestNet = monthly.reduce((b, m) => (m.net > b.net ? m : b), base);
    const topSpend = monthly.reduce((b, m) => (m.spend > b.spend ? m : b), base);
    return {
      income, spend, net: income - spend, billing, ledgerIncome,
      expenseRatio: income > 0 ? (spend / income) * 100 : 0,
      avgIncome: income / months, avgSpend: spend / months, avgNet: (income - spend) / months,
      billingMix: income > 0 ? (billing / income) * 100 : 0,
      ledgerMix: income > 0 ? (ledgerIncome / income) * 100 : 0,
      bestNet, topSpend,
    };
  }, [monthly]);

  // Net trend + forecast (solid history, dashed forecast, joined at the seam)
  const netChart = useMemo(() => {
    const hist = monthly.map((m) => ({ label: m.label, Net: Math.round(m.net), Forecast: null as number | null }));
    if (hist.length) hist[hist.length - 1].Forecast = hist[hist.length - 1].Net;
    const fore = projection.map((p) => ({ label: p.label, Net: null as number | null, Forecast: p.net }));
    return [...hist, ...fore];
  }, [monthly, projection]);

  const cmpLabel = labelOf(compareYm);
  const curLabel = labelOf(selectedMonth);

  const cmpData = [
    { metric: 'Income', Current: current.income, Compare: compare.income },
    { metric: 'Spend', Current: current.spend, Compare: compare.spend },
    { metric: 'Net', Current: current.net, Compare: compare.net },
  ];

  // ── styles ──
  const s: Record<string, React.CSSProperties> = {
    page: { padding: '1.75rem 2.25rem', maxWidth: 1760, margin: '0 auto', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
    title: { fontSize: '1.55rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' },
    sub: { fontSize: '0.85rem', color: '#64748b', marginTop: 3 },
    controls: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', background: 'white', border: '1px solid #eef1f6', borderRadius: 12, padding: '0.4rem 0.6rem', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' },
    input: { padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', background: 'white' },
    card: { background: 'white', borderRadius: 16, padding: '1.4rem 1.6rem', border: '1px solid #eef1f6', boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 16px 36px -26px rgba(15,23,42,0.4)' },
    cardTitle: { fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', marginBottom: '1rem' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.1rem', marginBottom: '1.5rem' },
    row2: { display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' },
    sectionLabel: { fontSize: '0.74rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: 8 },
  };
  const accentBar = <span style={{ width: 3, height: 14, borderRadius: 2, background: 'linear-gradient(180deg,#2563eb,#7c3aed)' }} />;

  const pill = (active: boolean): React.CSSProperties => ({
    padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
    borderColor: active ? '#2563eb' : '#e2e8f0', background: active ? '#2563eb' : 'white', color: active ? 'white' : '#64748b',
  });

  const navBtn = (disabled: boolean): React.CSSProperties => ({
    padding: '0.35rem 0.65rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '1rem', lineHeight: 1, color: disabled ? '#cbd5e1' : '#475569',
  });
  const atCurrentMonth = selectedMonth >= ymOf(new Date());

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Financial Dashboard</div>
          <div style={s.sub}>Income, spend &amp; net — with comparison and projection</div>
        </div>
        <div style={s.controls}>
          <label style={{ fontSize: '0.8rem', color: '#64748b' }}>Month</label>
          <button style={navBtn(false)} title="Previous month" onClick={() => setSelectedMonth(addMonth(selectedMonth, -1))}>‹</button>
          <input type="month" value={selectedMonth} max={ymOf(new Date())}
            onChange={(e) => e.target.value && setSelectedMonth(e.target.value)} style={s.input} />
          <button style={navBtn(atCurrentMonth)} title="Next month" disabled={atCurrentMonth}
            onClick={() => setSelectedMonth(addMonth(selectedMonth, 1))}>›</button>
          <button style={pill(compareMode === 'prev')} onClick={() => setCompareMode('prev')}>vs Previous</button>
          <button style={pill(compareMode === 'custom')} onClick={() => setCompareMode('custom')}>vs Custom</button>
          {compareMode === 'custom' && (
            <input type="month" value={customCompare} max={ymOf(new Date())}
              onChange={(e) => e.target.value && setCustomCompare(e.target.value)} style={s.input} />
          )}
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Loading…</div>}

      {!loading && (
        <>
          <div style={s.sectionLabel}>{accentBar} {curLabel} &nbsp;•&nbsp; compared to {cmpLabel}</div>
          <div style={s.kpiGrid}>
            <KPICard label="Income" value={fmtL(current.income)} color={INCOME_COLOR}
              delta={buildDelta(current.income, compare.income, true)} compareLabel={cmpLabel} />
            <KPICard label="Spend" value={fmtL(current.spend)} color={SPEND_COLOR}
              delta={buildDelta(current.spend, compare.spend, false)} compareLabel={cmpLabel} />
            <KPICard label="Net" value={fmtL(current.net)} color={current.net >= 0 ? NET_COLOR : '#dc2626'}
              delta={buildDelta(current.net, compare.net, true)} compareLabel={cmpLabel} />
            <KPICard label="Savings Rate"
              value={`${current.income > 0 ? ((current.net / current.income) * 100).toFixed(1) : '0.0'}%`}
              color="#7c3aed"
              delta={buildDelta(current.income > 0 ? current.net / current.income : 0, compare.income > 0 ? compare.net / compare.income : 0, true)}
              compareLabel={cmpLabel} />
          </div>

          {/* Income breakdown note */}
          <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginBottom: '1.25rem' }}>
            {curLabel} income = billing collections {fmtFull(current.billing)} + ledger credits {fmtFull(current.ledgerIncome)}
          </div>

          {/* Last 12 months roll-up — single full-width panel */}
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ ...s.sectionLabel, marginBottom: 0 }}>{accentBar} Last {HISTORY} months overview</div>
              <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{monthly[0]?.label} – {monthly[monthly.length - 1]?.label}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px, 1fr))', gap: '0.85rem' }}>
              <StatTile label="Total Income" value={fmtL(windowAgg.income)} color={INCOME_COLOR} />
              <StatTile label="Total Spend" value={fmtL(windowAgg.spend)} color={SPEND_COLOR} />
              <StatTile label="Total Net" value={fmtL(windowAgg.net)} color={windowAgg.net >= 0 ? NET_COLOR : '#dc2626'} />
              <StatTile label="Expense Ratio" value={`${windowAgg.expenseRatio.toFixed(1)}%`} color="#0891b2" />
              <StatTile label="Avg Income / mo" value={fmtL(windowAgg.avgIncome)} color={INCOME_COLOR} />
              <StatTile label="Avg Spend / mo" value={fmtL(windowAgg.avgSpend)} color={SPEND_COLOR} />
              <StatTile label="Avg Net / mo" value={fmtL(windowAgg.avgNet)} color={windowAgg.avgNet >= 0 ? NET_COLOR : '#dc2626'} />
              <StatTile label="Best Net Month" value={fmtL(windowAgg.bestNet.net)} note={windowAgg.bestNet.label} color={NET_COLOR} />
              <StatTile label="Highest Spend Month" value={fmtL(windowAgg.topSpend.spend)} note={windowAgg.topSpend.label} color={SPEND_COLOR} />
            </div>
          </div>

          {/* Trend */}
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={s.cardTitle}>Income vs Spend vs Net — last {HISTORY} months</div>
            {!hasData ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                No financial data in this window yet. Add ledger entries / record collections and it'll appear here.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '0.82rem', paddingTop: '0.5rem' }} />
                  <Bar dataKey="income" name="Income" fill={INCOME_COLOR} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="spend" name="Spend" fill={SPEND_COLOR} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Line dataKey="net" name="Net" stroke={NET_COLOR} strokeWidth={2.5} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Income composition */}
          {hasData && (
            <div style={{ ...s.card, marginBottom: '1.5rem' }}>
              <div style={s.cardTitle}>Income composition — billing collections vs ledger credits</div>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '0.82rem', paddingTop: '0.5rem' }} />
                  <Bar dataKey="billing" name="Billing collections" stackId="inc" fill="#2563eb" maxBarSize={30} />
                  <Bar dataKey="ledgerIncome" name="Ledger credits" stackId="inc" fill="#93c5fd" radius={[4, 4, 0, 0]} maxBarSize={30} />
                </ComposedChart>
              </ResponsiveContainer>
              <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginTop: 6 }}>
                Over the window: {windowAgg.billingMix.toFixed(0)}% from billing · {windowAgg.ledgerMix.toFixed(0)}% from ledger credits
              </div>
            </div>
          )}

          <div style={s.row2}>
            {/* Net projection */}
            <div style={s.card}>
              <div style={s.cardTitle}>Net trend &amp; {FORECAST}-month projection</div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={netChart} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line dataKey="Net" name="Net (actual)" stroke={NET_COLOR} strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false} />
                  <Line dataKey="Forecast" name="Net (projected)" stroke={NET_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginTop: 6 }}>
                Linear trend on the last {HISTORY} months. Needs a few months of data to be meaningful.
              </div>
            </div>

            {/* Comparison */}
            <div style={s.card}>
              <div style={s.cardTitle}>{curLabel} vs {cmpLabel}</div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={cmpData} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="metric" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: '0.5rem' }} />
                  <Bar dataKey="Current" name={curLabel} fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={34} />
                  <Bar dataKey="Compare" name={cmpLabel} fill="#cbd5e1" radius={[4, 4, 0, 0]} maxBarSize={34} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Projection table */}
          <div style={s.card}>
            <div style={s.cardTitle}>Projected next {FORECAST} months</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${projection.length}, 1fr)`, gap: '1rem' }}>
              {projection.map((p) => (
                <div key={p.ym} style={{ border: '1px dashed #cbd5e1', borderRadius: 10, padding: '0.9rem 1rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{p.label}</div>
                  <div style={{ fontSize: '0.76rem', color: '#64748b' }}>Income <span style={{ float: 'right', fontWeight: 600, color: INCOME_COLOR }}>{fmtL(p.income)}</span></div>
                  <div style={{ fontSize: '0.76rem', color: '#64748b' }}>Spend <span style={{ float: 'right', fontWeight: 600, color: SPEND_COLOR }}>{fmtL(p.spend)}</span></div>
                  <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 4, borderTop: '1px solid #f1f5f9', paddingTop: 4 }}>Net <span style={{ float: 'right', fontWeight: 700, color: p.net >= 0 ? NET_COLOR : '#dc2626' }}>{fmtL(p.net)}</span></div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FinancialDashboard;
